import { useEffect, useRef, useState } from "react";
import { Moon, Search, Sun } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import { Input } from "@components/ui/input";
import { Switch } from "@components/ui/switch";
import { Label } from "@components/ui/label";
import { useTheme } from "@shared-kernel/primary/layout/theme-provider";
import { ComponentsSection } from "@shared-kernel/primary/shared/design-system/sections/components.section";
import { CustomComponentsSection } from "@shared-kernel/primary/shared/design-system/sections/custom-components.section";
import { FoundationsSection } from "@shared-kernel/primary/shared/design-system/sections/foundations.section";
import { PatternsSection } from "@shared-kernel/primary/shared/design-system/sections/patterns.section";
import { DesignSystemFooter } from "@shared-kernel/primary/shared/design-system/shared/design-system-footer";
import { SearchProvider } from "@shared-kernel/primary/shared/design-system/shared/search-provider";
import { ShowcaseSidebar } from "@shared-kernel/primary/shared/design-system/shared/showcase-sidebar";

export const DesignSystemContent = () => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("foundations");
  const tabsContentRef = useRef<HTMLDivElement>(null);
  const isSearching = searchQuery.trim().length > 0;

  const previousTabRef = useRef(activeTab);
  useEffect(() => {
    if (previousTabRef.current === activeTab) return;
    previousTabRef.current = activeTab;
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    // Defer past child mount-time scroll side-effects (e.g. cmdk's Command auto-selects
    // its first item and calls scrollIntoView on it).
    const raf = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
    return () => cancelAnimationFrame(raf);
  }, [activeTab]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Design System</h1>
          <p className="text-sm text-muted-foreground">
            Référentiel vivant des tokens, composants et patterns utilisés dans l'application. Sert de source de vérité partagée entre code
            et documentation.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2">
          <Sun className="size-4 text-muted-foreground" />
          <Switch
            id="ds-theme-toggle"
            checked={isDark}
            onCheckedChange={checked => setTheme(checked ? "dark" : "light")}
            aria-label="Basculer le thème sombre"
          />
          <Moon className="size-4 text-muted-foreground" />
          <Label htmlFor="ds-theme-toggle" className="sr-only">
            Thème
          </Label>
        </div>
      </header>

      <div className="relative max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          type="search"
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          placeholder="Rechercher un composant…"
          aria-label="Rechercher un composant dans le design system"
          className="pl-9"
        />
      </div>

      <SearchProvider query={searchQuery}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {!isSearching && (
            <TabsList>
              <TabsTrigger value="foundations">Foundations</TabsTrigger>
              <TabsTrigger value="components">Composants shadcn</TabsTrigger>
              <TabsTrigger value="custom">Composants maison</TabsTrigger>
              <TabsTrigger value="patterns">Patterns</TabsTrigger>
            </TabsList>
          )}

          <div className="pt-4 lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-8">
            <div ref={tabsContentRef} className="min-w-0">
              {isSearching ? (
                <div className="space-y-10">
                  <FoundationsSection />
                  <ComponentsSection />
                  <CustomComponentsSection />
                  <PatternsSection />
                </div>
              ) : (
                <>
                  <TabsContent value="foundations" className="mt-0">
                    <FoundationsSection />
                  </TabsContent>
                  <TabsContent value="components" className="mt-0">
                    <ComponentsSection />
                  </TabsContent>
                  <TabsContent value="custom" className="mt-0">
                    <CustomComponentsSection />
                  </TabsContent>
                  <TabsContent value="patterns" className="mt-0">
                    <PatternsSection />
                  </TabsContent>
                </>
              )}
            </div>
            <aside className="hidden lg:block">
              <ShowcaseSidebar
                containerRef={tabsContentRef}
                refreshKey={isSearching ? `search:${searchQuery}` : `${activeTab}:${searchQuery}`}
              />
            </aside>
          </div>
        </Tabs>
      </SearchProvider>

      <DesignSystemFooter />
    </div>
  );
};
